'use client';

import React from 'react';
import { motion } from 'framer-motion';
import type { ParsedAdminApiError } from '@/lib/admin-api-error';
import { AdminApiErrorBanner } from '@/components/admin/AdminApiErrorBanner';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  /** Backward-compatible alias used by dashboard workspace pages. */
  description?: string;
  actions?: React.ReactNode;
  /** Optional breadcrumb row rendered above the page header (aligned to content width). */
  breadcrumb?: React.ReactNode;
  /** When true, skips the default title/subtitle header block (use a custom hero in children). */
  hideDefaultHeader?: boolean;
  loadError?: ParsedAdminApiError | null;
  onRetryLoad?: () => void;
}

export function AdminLayout({ 
  children, 
  title, 
  subtitle, 
  description,
  actions,
  breadcrumb,
  hideDefaultHeader = false,
  loadError,
  onRetryLoad,
}: AdminLayoutProps) {
  const headerSubtitle = subtitle ?? description;

  return (
    <div className={`max-w-7xl mx-auto pb-20 ${hideDefaultHeader ? 'space-y-5' : 'space-y-12'}`}>
      {breadcrumb ? <div className="space-y-2">{breadcrumb}</div> : null}
      {!hideDefaultHeader ? (
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-nature-muted/10 pb-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.15 }}
            className="border-l-4 border-nature-accent pl-6 py-2"
          >
            <h1 className="dashboard-display-title text-4xl font-bold tracking-tight text-soot mb-2">{title}</h1>
            {headerSubtitle ? (
              <p className="text-moss font-medium text-lg leading-relaxed max-w-2xl">{headerSubtitle}</p>
            ) : null}
          </motion.div>

          <div className="flex flex-col items-end gap-4">
            {actions ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center gap-4"
              >
                {actions}
              </motion.div>
            ) : null}
          </div>
        </div>
      ) : null}

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className={hideDefaultHeader ? 'space-y-5' : 'space-y-6'}
      >
        {loadError ? <AdminApiErrorBanner error={loadError} onRetry={onRetryLoad} /> : null}
        {children}
      </motion.div>
    </div>
  );
}
